import React, { useEffect, useState } from 'react';
import { Badge, Spinner } from 'react-bootstrap';
import { FaArrowLeft, FaQuestion } from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';

const InstructorSurveyQuestions = () => {
  const { id }   = useParams();
  const navigate = useNavigate();
  const [survey,    setSurvey]    = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading,   setLoading]   = useState(true);

  useEffect(() => {
    Promise.all([
      api.surveys.getById(id),
      api.surveys.getQuestions(id)
    ])
      .then(([sRes, qRes]) => {
        setSurvey(sRes.data?.data || sRes.data);
        const qs = qRes.data?.data || qRes.data?.questions || qRes.data || [];
        setQuestions(Array.isArray(qs) ? qs : []);
      })
      .catch((err) => toast.error('Failed to load survey: ' + (err.response?.data?.message || err.message)))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className="flex justify-center py-32">
      <Spinner animation="border" variant="primary" style={{ width: 48, height: 48 }} />
    </div>
  );

  return (
    <div className="p-3 sm:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      <button
        className="flex items-center gap-2 text-purple-700 hover:text-purple-900 font-semibold mb-5 text-sm"
        onClick={() => navigate('/instructor/surveys')}
        tabIndex={0}
        aria-label="Back to surveys"
      >
        <FaArrowLeft /> Back to Surveys
      </button>

      {survey && (
        <div className="bg-white rounded-2xl shadow-lg border border-purple-100 p-5 mb-5">
          <h4 className="text-xl font-extrabold text-gray-800 mb-1">{survey.title}</h4>
          <p className="text-sm text-gray-500">{survey.description}</p>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-lg border border-purple-100 overflow-hidden">
        <div className="bg-gradient-to-r from-purple-500 to-red-600 px-5 py-3">
          <h6 className="text-white font-bold mb-0">Questions ({questions.length})</h6>
        </div>
        {questions.length === 0 ? (
          <div className="text-center py-10 text-gray-400">
            <FaQuestion size={32} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">No questions found for this survey.</p>
          </div>
        ) : (
          <div className="divide-y divide-purple-50">
            {questions.map((q, i) => (
              <div key={q.id} className="p-5">
                <div className="flex items-start gap-3 mb-2">
                  <span className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-400 to-red-500 flex items-center justify-center text-white text-xs font-bold shadow flex-shrink-0">
                    {i + 1}
                  </span>
                  <p className="font-semibold text-gray-800 text-sm">{q.question_text || q.text || q.question}</p>
                </div>
                {q.options && Array.isArray(q.options) && q.options.length > 0 && (
                  <div className="ml-10 grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                    {q.options.map((opt, j) => (
                      <div key={j} className="flex items-center gap-2 bg-purple-50/60 rounded-lg px-3 py-2 text-sm text-gray-600">
                        <span className="w-5 h-5 rounded-full border-2 border-purple-300 flex items-center justify-center text-xs text-purple-600 font-bold flex-shrink-0">
                          {String.fromCharCode(65 + j)}
                        </span>
                        {typeof opt === 'string' ? opt : (opt.text || opt.label || opt.option_text || JSON.stringify(opt))}
                      </div>
                    ))}
                  </div>
                )}
                <div className="ml-10 mt-2">
                  <Badge bg="secondary" className="text-xs capitalize">{q.type || q.question_type || 'text'}</Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default InstructorSurveyQuestions;
